

import { formatCurrencyFull } from "@/lib/data";

/**
 * Reusable Report Table Component
 * 
 * This component can be used across all report pages for consistent table rendering.
 * 
 * Usage Example:
 * ```tsx
 * <ReportTable
 *   columns={[
 *     { key: 'name', label: 'Name', width: '30%' },
 *     { key: 'amount', label: 'Amount', align: 'right' },
 *   ]}
 *   data={reportData}
 *   totalField="amount"  // Optional: shows total row
 *   onRowClick={(item) => handleItemClick(item)}  // Optional: makes rows clickable
 *   formatValue={(value, key) => key === 'amount' ? formatCurrency(value) : value}  // Optional: custom formatting
 * />
 * ```
 * 
 * Props:
 * - columns: Array of column definitions with key, label, align (left/right/center), and optional width
 * - data: Array of data objects to display
 * - totalField: Optional field name to calculate and display total row
 * - onRowClick: Optional callback when row is clicked (makes rows clickable)
 * - formatValue: Optional function to customize cell rendering
 */
export function ReportTable({ 
  columns, 
  data, 
  totalField,
  onRowClick,
  formatValue 
}: { 
  columns: Array<{ key: string; label: string; align?: 'left' | 'right' | 'center'; width?: string }>;
  data: any[];
  totalField?: string;
  onRowClick?: (item: any) => void;
  formatValue?: (value: any, key: string) => React.ReactNode;
}) {
  const total = totalField && data.length > 0 
    ? data.reduce((sum, item) => sum + (parseFloat(item[totalField]) || 0), 0)
    : 0;

  return (
    <div className="bg-white rounded-xl border border-stone-200 overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-stone-50">
            <tr>
              {columns.map((col) => (
                <th 
                  key={col.key}
                  className={`px-4 py-3 text-xs font-semibold text-stone-500 ${
                    col.align === 'right' ? 'text-right' : col.align === 'center' ? 'text-center' : 'text-left'
                  }`}
                  style={col.width ? { width: col.width } : undefined}
                >
                  {col.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-stone-100">
            {data.map((item, index) => (
              <tr 
                key={item.id || index} 
                className={`hover:bg-stone-50 transition-colors ${onRowClick ? 'cursor-pointer' : ''}`}
                onClick={() => onRowClick?.(item)}
              >
                {columns.map((col) => (
                  <td 
                    key={col.key}
                    className={`px-4 py-3 text-sm ${
                      col.align === 'right' ? 'text-right' : col.align === 'center' ? 'text-center' : 'text-left'
                    }`}
                  >
                    {formatValue ? formatValue(item[col.key], col.key) : item[col.key]}
                  </td>
                ))}
              </tr>
            ))}
            {totalField && data.length > 0 && (
              <tr className="bg-stone-50 border-t-2 border-stone-200">
                <td 
                  colSpan={columns.length - 1}
                  className="px-4 py-3 text-sm font-bold text-stone-800 text-right"
                >
                  Total:
                </td>
                <td className="px-4 py-3 text-sm font-bold text-emerald-600 text-right">
                  {formatCurrencyFull(total)}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
